import {isLoginCallbackReturnTo} from "./login-return-to.js";
import type {LoginCompletion} from "./deployment-config.js";

/**
 * Login completion policy, shared by the server's redirect producers and the SPA.
 *
 * A finished login hands the browser to a return target, and for the product
 * frontends (TwoShot's /login/callback) that hand-off carries the first-party
 * access and refresh tokens in the URL fragment. This module decides WHEN a
 * target may carry them.
 *
 * A RETURN TARGET ON THE AUTH ORIGIN NEVER CARRIES TOKENS. When a browser
 * follows a redirect whose Location has no fragment, it carries the CURRENT
 * fragment onto the new URL (RFC 9110 §10.2.2). The auth origin's own pages
 * redirect onward: /oauth2/authorize sends the browser to an OAuth client's
 * redirect_uri, and /oauth/<provider>/start sends it to a provider. A token
 * pair appended to an auth-origin return_to therefore rides those hops into a
 * third party's URL. A trusted or already-consented client received the
 * user's FIRST-PARTY token pair alongside its authorization code this way. The
 * auth origin authenticates by its own cookies (the SSO session and
 * oauth_session), which every rail sets before it completes, so a return to it
 * needs no credentials in the URL. (The magic-link hand-off to the SPA's own
 * /login/done is not a return target: the SPA strips that fragment from
 * history before it navigates anywhere.)
 *
 * ONLY A LOGIN CALLBACK RECEIVES THEM (isLoginCallbackReturnTo). The return-to
 * allowlist admits any page on any product host as a landing place, but several
 * of those pages redirect off-site (twoshot.app/discord -> discord.gg,
 * /codeck -> docsend.com) and a fragment rides that redirect too, while other
 * subdomains are third-party hosted. Every other admitted target, docs.twoshot.app
 * included, authenticates by the SSO cookie and is reached with no credential.
 *
 * A RELYING-PARTY DEPLOYMENT NEVER RECEIVES THEM EITHER (returnTo.completion
 * "relying-party", Forge). Its frontend has no fragment consumer: it signs in
 * only through its own state/PKCE-bound authorization-code flow, and
 * /oauth2/authorize continues that flow from the SSO session the rail set. The
 * completion target is the product's login entry, reached with no credential in
 * the URL. A fragment there would be ignored at best, and carried through the
 * product's own redirects at worst.
 *
 * "The auth origin" is every origin the service answers sign-ins on: the
 * canonical one (AUTH_BASE_URL) first, then any legacy sign-in host
 * (AUTH_LEGACY_HOSTS), whose sign-in pages redirect onward in the same way.
 *
 * Fails closed: only "token-fragment" carries the pair, a target that cannot be
 * parsed or is relative never does, and an auth origin never does even if it
 * also served a callback path.
 */
export function loginCompletionMayCarryTokens(
    returnTo: string,
    authOrigins: string | readonly string[],
    completion: LoginCompletion,
): boolean {
    if (completion !== "token-fragment") return false;
    const auth = (typeof authOrigins === "string" ? [authOrigins] : authOrigins).map(o => new URL(o).origin);
    if (auth.length === 0) return false;
    try {
        return isLoginCallbackReturnTo(returnTo) && !auth.includes(new URL(returnTo, auth[0]).origin);
    } catch {
        return false;
    }
}

/**
 * The Location for a redirect, with a fragment of its own so the browser cannot
 * carry the current one onto it (RFC 9110 §10.2.2). An EMPTY fragment is still
 * a fragment: `https://client/cb?code=x#` replaces whatever the previous URL
 * carried, while `https://client/cb?code=x` would inherit it. A Location that
 * already names a fragment is returned unchanged.
 */
export function withExplicitFragment(location: string): string {
    return location.includes("#") ? location : `${location}#`;
}
